import React, { useMemo, useCallback } from 'react';
import { CircularProgress, Typography, Tooltip, Box } from '@mui/material';
import {
  VisibilityOutlined as ViewIcon,
  PaymentOutlined as PaymentIcon,
  UndoOutlined as ReturnIcon,
  ReceiptOutlined as ReceiptIcon,
  SwapHorizOutlined as ExchangeIcon,
  Edit as EditOrderIcon,
} from '@mui/icons-material';
import { SalesOrderResponseDto } from '../../../../api';
import { useOrdersTable } from './OrdersTable.hooks';
import {
  OrderCode,
  TotalText,
  DateText,
  LoadingContainer,
  ErrorContainer,
} from './OrdersTable.styles';
import { BaseTable, TableColumn } from '../../../common/Table';
import { StatusChip, ActionButton } from '../../../common/Table/Table.styles';
import { mapOrderStatus } from '../../../common/status';
import {
  getOrderFulfillmentPresentation,
  getFulfillmentProgressCaption,
} from '../../../../utils/sales/saleFulfillmentPresentation';
import { useOrdersPermissions } from '../../../../hooks/useOrdersPermissions';
import { formatCommercialSaleDate } from '../../../../utils/sales/commercial-sale-date';
import {
  getSalesOrderCustomerDisplayName,
  hasSalesOrderCustomer,
} from '../../../../utils/sales/sales-order-customer';
import { OrdersTableExpandedRow } from './OrdersTableExpandedRow';
import { BRANCH_RECEIPT_NUMBERING_ENABLED } from '../../../../config/feature-flags';
import {
  BRANCH_RECEIPT_NUMBER_LABEL,
  SALES_ORDER_NUMBER_LABEL,
} from '../../../../utils/sales/branch-receipt-numbering';
import { mapOrderDeliveryStatus } from '../../../common/status/statusMap';
import { canEditSalesOrderCommercial } from '../../../../utils/sales/sales-order-actions';

interface OrdersTableProps {
  orders: SalesOrderResponseDto[];
  loading: boolean;
  error?: string | null;
  selectedOrders: number[];
  setSelectedOrders: (orders: number[]) => void;
  /** Controlled expanded mixed-sale rows (lifted so list refresh can refetch only these). */
  expandedOrderIds: number[];
  onExpandedOrderIdsChange: (orderIds: number[]) => void;
  onViewOrder: (id: number) => void;
  onEditOrder: (id: number) => void;
  onDeliverOrder: (id: number) => void;
  /** Reservado: reimpresión desde historial (UI oculta por ahora). */
  onPrintReceipt?: (id: number) => void;
  onProcessPayment: (id: number) => void;
  onReturnOrder: (id: number, orderCode?: string) => void;
  onExchangeOrder?: (id: number) => void;
}

const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  loading,
  error,
  selectedOrders,
  setSelectedOrders,
  expandedOrderIds,
  onExpandedOrderIdsChange,
  onViewOrder,
  onEditOrder,
  onDeliverOrder,
  onPrintReceipt,
  onProcessPayment,
  onReturnOrder,
  onExchangeOrder,
}) => {
  // Orders permissions - centralized in a single hook
  const ordersPerms = useOrdersPermissions();

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getStatusConfig,
    formatCurrency,
    canProcessPayment,
    canReturn,
    canExchange,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    hasOrders,
    handleViewOrder,
    handleEditOrder,
    handleProcessPayment,
    handleReturnOrder,
    handleExchangeOrder,
  } = useOrdersTable({
    orders,
    onViewOrder,
    onEditOrder,
    onDeliverOrder,
    onPrintReceipt,
    onProcessPayment,
    onReturnOrder,
    onExchangeOrder,
  });

  const columns: TableColumn<SalesOrderResponseDto>[] = useMemo(
    () => [
      {
        id: 'orderCode',
        label: SALES_ORDER_NUMBER_LABEL,
        sortAccessor: (order) => order.orderCode,
        render: (order) => <OrderCode variant="body2">{order.orderCode}</OrderCode>,
      },
      ...(BRANCH_RECEIPT_NUMBERING_ENABLED
        ? [
            {
              id: 'branchReceiptCode',
              label: BRANCH_RECEIPT_NUMBER_LABEL,
              sortAccessor: (order: SalesOrderResponseDto) => order.branchReceiptCode ?? '',
              render: (order: SalesOrderResponseDto) => (
                <Typography variant="body2" color="text.secondary">
                  {order.branchReceiptCode ?? '—'}
                </Typography>
              ),
            } as TableColumn<SalesOrderResponseDto>,
          ]
        : []),
      {
        id: 'customer',
        label: 'Cliente',
        sortAccessor: (order) => getSalesOrderCustomerDisplayName(order.customer),
        render: (order) => {
          const hasCustomer = hasSalesOrderCustomer(order.customer);
          return (
            <Typography
              variant="body2"
              color={hasCustomer ? 'text.primary' : 'text.secondary'}
              fontStyle={hasCustomer ? 'normal' : 'italic'}
            >
              {getSalesOrderCustomerDisplayName(order.customer)}
            </Typography>
          );
        },
      },
      {
        id: 'location',
        label: 'Sucursal',
        sortAccessor: (order) => order.location?.name,
        render: (order) => (
          <Typography variant="body2" color="text.secondary">
            {order.location?.name || 'Sucursal no asignada'}
          </Typography>
        ),
      },
      {
        id: 'totalAmount',
        label: 'Monto Total',
        sortAccessor: (order) => order.totalAmount,
        render: (order) => (
          <TotalText variant="body2">{formatCurrency(order.totalAmount)}</TotalText>
        ),
      },
      {
        id: 'saleDate',
        label: 'Fecha de Venta',
        sortAccessor: (order) => order.saleDate,
        render: (order) => (
          <DateText variant="body2">
            {formatCommercialSaleDate(order, 'es-ES', { includeTime: true })}
          </DateText>
        ),
      },
      {
        id: 'status',
        label: 'Estado',
        sortAccessor: (order) => order.status,
        render: (order) => {
          const chip = mapOrderStatus(order.status);
          const deliveryChip = mapOrderDeliveryStatus(order.deliveryStatus ?? 'NONE');
          const presentation = getOrderFulfillmentPresentation(order.fulfillmentStatus);
          const progress = getFulfillmentProgressCaption(
            order.fulfillmentStatus,
            order.fulfillmentSummary,
          );
          return (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}
            >
              <StatusChip label={chip.label} color={chip.color} size="small" />
              <StatusChip label={deliveryChip.label} color={deliveryChip.color} size="small" />
              {presentation && (
                <Tooltip title={presentation.helperText}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <StatusChip
                      label={presentation.label}
                      status={presentation.statusType as any}
                      visualStyle={presentation.visualStyle as any}
                      withIcon={presentation.withIcon}
                      size="small"
                    />
                    {progress && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.25, lineHeight: 1 }}
                      >
                        {progress}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ],
    [formatCurrency],
  );

  const handleSelectItems = useCallback(
    (items: (string | number)[]) => setSelectedOrders(items as number[]),
    [setSelectedOrders],
  );

  const isRowExpandable = useCallback(
    (order: SalesOrderResponseDto) => order.fulfillmentStatus !== 'NOT_REQUIRED',
    [],
  );

  const handleToggleExpand = useCallback(
    (itemId: string | number) => {
      const id = itemId as number;
      onExpandedOrderIdsChange(
        expandedOrderIds.includes(id)
          ? expandedOrderIds.filter((expandedId) => expandedId !== id)
          : [...expandedOrderIds, id],
      );
    },
    [expandedOrderIds, onExpandedOrderIdsChange],
  );

  if (!ordersPerms.canView) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  }

  // Error state
  if (error) {
    return (
      <ErrorContainer>
        <Typography variant="h6" color="error" gutterBottom>
          Error al cargar las ventas
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
      </ErrorContainer>
    );
  }

  return (
    <BaseTable<SalesOrderResponseDto>
      items={orders}
      columns={columns}
      selectedItems={selectedOrders}
      onSelectItems={handleSelectItems}
      showCheckbox={false}
      showActions
      getItemId={(order) => order.id}
      expandedItemIds={expandedOrderIds}
      onToggleExpand={handleToggleExpand}
      isRowExpandable={isRowExpandable}
      renderExpandedContent={(order) => <OrdersTableExpandedRow orderId={order.id} />}
      emptyStateTitle="No se encontraron ventas"
      emptyStateSubtitle={
        ordersPerms.canAdd ? 'Ajuste los filtros o cree una nueva venta.' : 'Ajuste los filtros.'
      }
      emptyStateIcon={<ReceiptIcon />}
      sortBy="saleDate"
      sortOrder="desc"
      renderActions={(order) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="Ver detalles">
            <ActionButton size="small" onClick={() => handleViewOrder(order.id)}>
              <ViewIcon fontSize="small" />
            </ActionButton>
          </Tooltip>
          {ordersPerms.canEdit && canEditSalesOrderCommercial(order.status) && (
            <Tooltip title="Editar venta">
              <ActionButton size="small" onClick={() => handleEditOrder(order.id)}>
                <EditOrderIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          )}
          {canProcessPayment(order.status) && ordersPerms.canPay && (
            <Tooltip title="Procesar pago">
              <ActionButton size="small" onClick={() => handleProcessPayment(order.id)}>
                <PaymentIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          )}
          {canReturn(order.status) && ordersPerms.canReturn && (
            <Tooltip title="Procesar devolución">
              <ActionButton
                size="small"
                onClick={() => handleReturnOrder(order.id, order.orderCode)}
              >
                <ReturnIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
          )}
          {canExchange(order.id, order.status, order.fulfillmentStatus, order.deliveryStatus) &&
            onExchangeOrder &&
            ordersPerms.canExchange && (
              <Tooltip title="Intercambiar piezas">
                <ActionButton size="small" onClick={() => handleExchangeOrder(order.id)}>
                  <ExchangeIcon fontSize="small" />
                </ActionButton>
              </Tooltip>
            )}
        </Box>
      )}
    />
  );
};

export default OrdersTable;
